import { inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { type AccountInfo } from '@azure/msal-browser';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { ENV_CONFIG, EnvConfig } from '@ottobock/shared/domain';

export enum UserRole {
  UpdateManager = 'RemoteServices_UpdateManager',
  User = 'RemoteServices_User',
  AppDeveloper = 'RemoteServices_AppDeveloper'
}

export interface AppAccountInfo extends AccountInfo {
  idTokenClaims: {
    access?: string[];
    acr: string;
    aud: string;
    auth_time: number;
    country: string;
    email: string;
    exp: number;
    family_name: string;
    given_name: string;
    iat: number;
    iss: string;
    nbf: number;
    nonce: string;
    roles?: UserRole[];
    scopes?: string[];
    sub: string;
    tid: string;
    ver: string;
  };
}

export interface AuthStoreType {
  isAuthenticated: boolean;
  isLoggingIn: boolean;
  accountInfo: AppAccountInfo | null;
  accessToken: string | null;
  error: unknown;
}

export const initialState: AuthStoreType = {
  isAuthenticated: false,
  isLoggingIn: false,
  accountInfo: null,
  accessToken: null,
  error: null
};

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods(store => {
    const msalService = inject(MsalService);
    const { authEnv } = inject<EnvConfig>(ENV_CONFIG);
    let pendingRefresh: Promise<string | null> | null = null;

    function getActiveAccount(): AccountInfo | null {
      return msalService.instance.getActiveAccount()
        ?? msalService.instance.getAllAccounts()[0]
        ?? null;
    }

    return {
      setAuth(accountInfo: AppAccountInfo) {
        patchState(store, {
          accountInfo
        });
      },

      setIsLoggingIn(isLoggingIn: boolean) {
        patchState(store, { isLoggingIn });
      },

      async hydrateFromMsal() {
        const account = getActiveAccount();

        if (account) {
          this.setAuth(account as AppAccountInfo);
        } else this.clearAuth();
      },

      async acquireAccessTokenSilently(activeAccount: AccountInfo, forceRefresh = false) {
        try {
          const result = await msalService.instance.acquireTokenSilent({
            account: activeAccount,
            scopes: authEnv.apiConfig.scopes,
            forceRefresh
          });

          patchState(store, state => ({
            ...state,
            isAuthenticated: true,
            accessToken: result.accessToken,
            error: null
          }));
        } catch (error) {
          patchState(store, state => ({
            ...state,
            error
          }));
          throw error;
        }
      },

      async waitForAccessToken(): Promise<string | null> {
        const account = getActiveAccount();
        if (!account) return null;

        await this.acquireAccessTokenSilently(account);
        return store.accessToken();
      },

      async forceRefreshAccessToken(): Promise<string | null> {
        if (pendingRefresh) return pendingRefresh;

        const account = getActiveAccount();
        if (!account) return null;

        pendingRefresh = this.acquireAccessTokenSilently(account, true)
          .then(() => store.accessToken())
          .catch(() => null)
          .finally(() => { pendingRefresh = null; });

        return pendingRefresh;
      },

      logout() {
        this.clearAuth();
        msalService.logoutRedirect();
      },

      clearAuth() {
        patchState(store, {
          isAuthenticated: false,
          accountInfo: null,
          isLoggingIn: false,
          accessToken: null,
          error: null
        });
      }
    };
  }),
  withComputed(state => ({
    userInitials: () => {
      const tokenClaims = state.accountInfo()?.idTokenClaims;
      if (!tokenClaims) return '';
      return `${tokenClaims.given_name.charAt(0)}${tokenClaims.family_name.charAt(0)}`;
    },

    userRoles: () => {
      const tokenClaims = state.accountInfo()?.idTokenClaims;
      if (!tokenClaims) return [];
      return tokenClaims.roles ?? [];
    },

    userAccess: () => {
      const tokenClaims = state.accountInfo()?.idTokenClaims;
      if (!tokenClaims) return [];
      return tokenClaims.access ?? [];
    },

    idToken: () => {
      const idToken = state.accountInfo()?.idToken;
      if (!idToken) return '';
      return idToken;
    },
    userFullName: () => {
      const tokenClaims = state.accountInfo()?.idTokenClaims;
      if (!tokenClaims) return '';
      return `${tokenClaims.given_name} ${tokenClaims.family_name}`;
    },

    userEmail: () => {
      const tokenClaims = state.accountInfo()?.idTokenClaims;
      if (!tokenClaims) return '';
      return tokenClaims.email;
    }
  }))
);
